import { Typography, CircularProgress, DialogContent, Dialog, Stack } from '@mui/material';
import { PaymentStatus } from './paymentEnum';
import { useCallback, useEffect, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';

interface LoadingProps {
  open: boolean;
  onClose: () => void;
  paymentStatus: PaymentStatus;
  autoCloseDelay: number;
}

export function PaymentDialog(props: LoadingProps) {
  const { onClose, open, paymentStatus, autoCloseDelay } = props;
  const [dialogContent, setDialogContent] = useState<React.JSX.Element>();
  const [disableClose, setDisableClose] = useState<boolean>(true);
  const { t } = useTranslation();

  const successContent = useCallback(() => {
    return (
      <Stack sx={{ height: '150px' }} direction="column" justifyContent="center" alignItems="center">
        <CheckCircleIcon sx={{ color: 'success.light', height: '50px', width: '50px' }} />
        <Typography sx={{ color: 'success.light', mt: '10px', textAlign: 'center' }}>{t('payment:success')}</Typography>
      </Stack>
    );
  }, [t]);

  const loadingContent = useCallback(() => {
    return (
      <Stack sx={{ height: '150px' }} direction="column" justifyContent="center" alignItems="center">
        <CircularProgress sx={{ color: 'progress.circular.secondary' }} />
        <Typography sx={{ color: 'secondary.main', mt: '10px' }}>{t('payment:loading')}</Typography>
      </Stack>
    );
  }, [t]);

  const processPaymentStatus = useCallback(() => {
    switch (paymentStatus) {
      case PaymentStatus.Loading:
        setDisableClose(true);
        setDialogContent(loadingContent());
        break;
      case PaymentStatus.Success:
        setDisableClose(false);
        setDialogContent(successContent());
        setTimeout(function () {
          onClose();
        }, autoCloseDelay);
        break;
      case PaymentStatus.Failure:
        setDisableClose(false);
        setDialogContent(failureContent(t('payment:failure')));
        setTimeout(function () {
          onClose();
        }, autoCloseDelay);
        break;
      case PaymentStatus.FailedSubmitOrder:
        setDisableClose(false);
        setDialogContent(failureContent(t('payment:failedSubmitOrder')));
        break;
      default:
        setDisableClose(true);
        setDialogContent(loadingContent());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus]);

  useEffect(() => {
    processPaymentStatus();
  }, [processPaymentStatus]);

  const failureContent = (message: string) => {
    return (
      <Stack sx={{ height: '150px' }} direction="column" justifyContent="center" alignItems="center">
        <CancelIcon sx={{ color: '#ff6e63', height: '50px', width: '50px' }} />
        <Typography sx={{ color: '#ff6e63', mt: '10px' }}>{message}</Typography>
      </Stack>
    );
  };

  return (
    <Dialog
      fullWidth
      open={open}
      disableEscapeKeyDown={disableClose}
      onClose={() => {
        if (!disableClose) {
          onClose();
        }
      }}
    >
      <DialogContent>{dialogContent}</DialogContent>
    </Dialog>
  );
}
